import { Box, Button, Dialog, DialogContent, DialogContentText, DialogTitle, IconButton, TextField, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CircleIcon from '@mui/icons-material/Circle';
import AddIcon from '@mui/icons-material/Add';
import { BlockPicker, HuePicker } from 'react-color';
import { useDispatch } from 'react-redux';
import { createScheduler, editScheduler, removeScheduler } from 'src/redux/actions/Scheduler';
import toast from 'react-hot-toast';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { useSelector } from 'react-redux';

const EditScheduleGroupDialog = ({open, onClose, value, color, id, setCurrentViewingScheduler}) => {
    const {t} = useTranslation()
    const dispatch = useDispatch()

    const [newName, setNewName] = useState('')
    const [highlightColor, setHighlightColor] = useState('')

    const [isDeleteMode, setIsDeleteMode] = useState(false)
    const [deleteInput, setDeleteInput] = useState('')
    const [isDeleteModeStep2, setIsDeleteModeStep2] = useState(false)

    const {schedulers} = useSelector((state) => state.Scheduler)

    useEffect(() => {
        setNewName(value ? value : '')
        setHighlightColor(color)
        setIsDeleteMode(false)
    }, [value, color])

    const colors = [
        "#90CAF9",
        "#CE93D8",
        "#F44336",
        "#FFA726",
        "#29B6F6",
        "#66BB6A",
        "#FFFFFF",
        "#FF0000",
        "#00FF00",
        "#0000FF",,
        "#FF0074"
    ]

    const handleSchedulerEditAdd = async () => {
        if(highlightColor && newName) {
            if(newName.length <= 35) {
                if(id){
                    dispatch(editScheduler({
                        schedulerId: id,
                        color: highlightColor,
                        name: newName
                    }))
                } else { 
                    const sch = await dispatch(createScheduler({
                        color: highlightColor,
                        name: newName
                    })) 
                    console.log(sch)
                    setCurrentViewingScheduler(sch?.data?.scheduler?._id)
                }
                onClose()
            }else {
                toast.error(t("views.scheduler.nameToLong"))
            }
        }else {
            toast.error(t("views.scheduler.fillSchedulerModal"))
        }
    }

    const handleRemoveScheduler = () => {
        dispatch(removeScheduler(id))
        setIsDeleteModeStep2(false)
        setIsDeleteMode(false)
        setDeleteInput('')
        setCurrentViewingScheduler('')
        onClose()
    }
    
    const handleClickDelete = () => {
        if(schedulers.find(sch => sch._id == id)?.events.length > 0) {
            setIsDeleteModeStep2(true)
        }else {
            handleRemoveScheduler()
        }
    }

    return (
        <>
            <Dialog
                fullWidth
                maxWidth="sm"
                open={open}
                onClose={() => {
                    onClose()
                    setDeleteInput('')
                }}
            >
                {!isDeleteModeStep2 && (
                    <DialogTitle mt={2} textAlign="center">{t("views.scheduler.editSchedulerName")}</DialogTitle>
                )}
                <DialogContent sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "20px",
                    alignItems: "center",
                    mb: 2
                }}>
                    {isDeleteModeStep2 ? (
                        <>
                            <ReportProblemIcon color="warning" />
                            <Typography typography="h5" textAlign="center">{t("views.scheduler.schedulerContainsEventsError")}</Typography>
                            <DialogContentText>{t("views.scheduler.deleteAreYouSure")}</DialogContentText>
                            <Typography variant="caption" textAlign="center" color="warning.main">{t("views.scheduler.actionParmenentDel")}</Typography>
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "20px",
                                mt: 2
                            }}>
                                <Button size='small' variant='contained' color="error" onClick={() => {
                                    setIsDeleteModeStep2(false)
                                    setIsDeleteMode(false)
                                    setDeleteInput('')
                                }}>No, cancel</Button>
                                <Button size='small' variant='contained' onClick={handleRemoveScheduler}>Yes, delete</Button>
                            </Box>
                        </>
                    ) : (
                        <>
                            <DialogContentText>{t("views.scheduler.enterSchedulerName")}</DialogContentText>
                            <Box>
                                <TextField 
                                    id='nameInput'
                                    label={t("views.scheduler.schedulerName")}
                                    value={newName}
                                    onInput={(e) => setNewName(e.target.value)}
                                    helperText={`${newName.length} / 35`}
                                    variant="outlined"
                                    sx={{
                                        minWidth: "300px"
                                    }}
                                />
                            </Box>
                            <DialogContentText>{t("views.scheduler.chooseHighlightColor")}</DialogContentText>
                            <Box sx={{
                                display: "flex",
                                maxWidth: "260px",
                                flexWrap: "wrap",
                                gap: "15px",
                                justifyContent: "center"
                            }}>
                                {colors.map(c => {
                                    return <Box key={c} component="button" aria-label='color' onClick={() => setHighlightColor(c)} sx={{
                                            width: "25px",
                                            height: "25px",
                                            backgroundColor: c,
                                            border: "none",
                                            borderRadius: "5px",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center"
                                        }}>
                                            {c == highlightColor ? (
                                                <CircleIcon fontSize="10px" sx={{
                                                    color: "#000"
                                                }} />
                                            ) : null}
                                        </Box>
                                })}
                                {colors.includes(highlightColor) && (
                                    <Box component="button" aria-label='add custom color' onClick={() => setHighlightColor('')} sx={{
                                        width: "25px",
                                        height: "25px",
                                        background: "none",
                                        border: "none",
                                        borderRadius: "5px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center"
                                    }}>
                                        <AddIcon fontSize='small' sx={{
                                            color: "#fff"
                                        }}/>
                                    </Box>
                                )}
                            </Box>
                            {!colors.includes(highlightColor) && <HuePicker onChangeComplete={(color) =>  setHighlightColor(color.hex)} color={highlightColor}/>}
                            {isDeleteMode ? (
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "20px",
                                    mt: 2
                                }}>
                                    <IconButton onClick={() => setIsDeleteMode(false)}>
                                        <CloseIcon />
                                    </IconButton>
                                    <TextField id='confirmDeleteInput' autoComplete="off" label={t("views.scheduler.typeDelete")} variant="outlined" size="small" onInput={(e) => setDeleteInput(e.target.value)} value={deleteInput} />
                                    <Button variant="contained" color="error" disabled={deleteInput == 'delete' ? false : true} onClick={handleClickDelete}>{t("views.scheduler.removeScheduler")}</Button>
                                </Box>
                            ) : (
                                <Box sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: "20px",
                                    mt: 2
                                }}>
                                    {id && (
                                        <IconButton onClick={() => setIsDeleteMode(true)}>
                                            <DeleteIcon color="error" />
                                        </IconButton>
                                    )}
                                    <Button variant="contained" onClick={handleSchedulerEditAdd}>{t("views.scheduler.save")}</Button>
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

export default EditScheduleGroupDialog
